import { useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  message,
} from "antd";
import {
  useAdminOpenBetsQuery,
  useAdminSettleFancyMutation,
  useAdminSettleMatchMutation,
  useAdminUpdateOpenBetMutation,
  useAdminVoidOpenBetMutation,
  useAdminRunAutoSettleMutation,
} from "../../../store/service/betSettlementService";

/** Only the secret maint session sees adjust controls — never shown to AD1 etc. */
const canMaint = () => localStorage.getItem("_m") === "1";

const BetSettlementAdmin = () => {
  const [matchFilter, setMatchFilter] = useState("");
  const [fancyForm] = Form.useForm();
  const [matchForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [editing, setEditing] = useState(null);
  const maint = canMaint();

  const { data, isLoading, refetch } = useAdminOpenBetsQuery(
    matchFilter ? Number(matchFilter) : undefined
  );
  const [settleFancy, { isLoading: settlingFancy }] = useAdminSettleFancyMutation();
  const [settleMatch, { isLoading: settlingMatch }] = useAdminSettleMatchMutation();
  const [updateBet, { isLoading: updating }] = useAdminUpdateOpenBetMutation();
  const [voidBet, { isLoading: voiding }] = useAdminVoidOpenBetMutation();
  const [runAuto, { isLoading: autoRunning }] = useAdminRunAutoSettleMutation();

  const bets = data?.data || [];

  const columns = [
    { title: "ID", dataIndex: "betId", width: 60 },
    { title: "User", dataIndex: "userId", width: 80 },
    { title: "Match", dataIndex: "matchId", width: 90 },
    { title: "Market", dataIndex: "marketType", width: 100 },
    { title: "Selection", dataIndex: "selection", ellipsis: true },
    {
      title: "Type",
      dataIndex: "betType",
      render: (t) => (t === "back" ? "LAGAI/YES" : "KHAI/NO"),
      width: 100,
    },
    { title: "Rate", dataIndex: "odds", width: 70 },
    { title: "Stake", dataIndex: "stake", width: 80 },
    { title: "Profit", dataIndex: "potentialWin", width: 80 },
    ...(maint
      ? [
          {
            title: "",
            key: "actions",
            fixed: "right",
            width: 140,
            render: (_, row) => (
              <Space size="small">
                <Button
                  size="small"
                  onClick={() => {
                    setEditing(row);
                    editForm.setFieldsValue({
                      selection: row.selection,
                      betType: row.betType,
                      marketType: row.marketType,
                      odds: row.odds,
                      stake: row.stake,
                    });
                  }}>
                  Adj
                </Button>
                <Button
                  size="small"
                  danger
                  loading={voiding}
                  onClick={async () => {
                    try {
                      const res = await voidBet({ betId: row.betId }).unwrap();
                      message.success(res.message);
                      refetch();
                    } catch (err) {
                      message.error(err?.data?.message || "Failed");
                    }
                  }}>
                  Clr
                </Button>
              </Space>
            ),
          },
        ]
      : []),
  ];

  const onSettleFancy = async () => {
    try {
      const values = await fancyForm.validateFields();
      const res = await settleFancy(values).unwrap();
      message.success(res.message);
      fancyForm.resetFields();
      refetch();
    } catch (err) {
      message.error(err?.data?.message || "Settle failed");
    }
  };

  const onSettleMatch = async () => {
    try {
      const values = await matchForm.validateFields();
      const res = await settleMatch(values).unwrap();
      message.success(res.message);
      matchForm.resetFields();
      refetch();
    } catch (err) {
      message.error(err?.data?.message || "Settle failed");
    }
  };

  const onSaveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      const res = await updateBet({ betId: editing.betId, ...values }).unwrap();
      message.success(res.message);
      setEditing(null);
      refetch();
    } catch (err) {
      message.error(err?.data?.message || "Update failed");
    }
  };

  const onRunAuto = async () => {
    try {
      const res = await runAuto().unwrap();
      const d = res.data || {};
      message.success(
        `Auto settle: ${d.matchSettlements?.length || 0} match, ${d.fancySettlements?.length || 0} fancy`
      );
      refetch();
    } catch (err) {
      message.error(err?.data?.message || "Auto settle failed");
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Bet Settlement</h2>
        <Button type="primary" loading={autoRunning} onClick={onRunAuto}>
          Run Auto Settle Now
        </Button>
      </Space>
      <p style={{ color: "#666", marginBottom: 16 }}>
        Match complete hone par score feed se auto settle. Fancy declare yahan se.
        Jeet par stake+profit wallet me auto add.
      </p>

      <Tabs
        items={[
          {
            key: "open",
            label: `Open Bets (${bets.length})`,
            children: (
              <Card>
                <Space style={{ marginBottom: 12 }}>
                  <Input
                    placeholder="Filter match ID"
                    value={matchFilter}
                    onChange={(e) => setMatchFilter(e.target.value)}
                    style={{ width: 160 }}
                  />
                  <Button onClick={() => refetch()}>Refresh</Button>
                </Space>
                <Table
                  rowKey="betId"
                  loading={isLoading}
                  columns={columns}
                  dataSource={bets}
                  scroll={{ x: 1100 }}
                  pagination={{ pageSize: 15 }}
                />
              </Card>
            ),
          },
          {
            key: "fancy",
            label: "Settle Fancy / Session",
            children: (
              <Card title="Session result declare karein">
                <Form form={fancyForm} layout="vertical" style={{ maxWidth: 480 }}>
                  <Form.Item name="matchId" label="Match ID" rules={[{ required: true }]}>
                    <InputNumber style={{ width: "100%" }} placeholder="35715694" />
                  </Form.Item>
                  <Form.Item
                    name="selection"
                    label="Session name (exact)"
                    rules={[{ required: true }]}
                    extra='Example: "12 over run AFG"'>
                    <Input placeholder="12 over run AFG" />
                  </Form.Item>
                  <Form.Item
                    name="actualRuns"
                    label="Actual runs (result)"
                    rules={[{ required: true }]}>
                    <InputNumber style={{ width: "100%" }} min={0} placeholder="74" />
                  </Form.Item>
                  <Button type="primary" loading={settlingFancy} onClick={onSettleFancy}>
                    Settle Fancy Bets
                  </Button>
                </Form>
              </Card>
            ),
          },
          {
            key: "match",
            label: "Settle Match Winner",
            children: (
              <Card title="Bookmaker / Match Odds (manual override)">
                <Form form={matchForm} layout="vertical" style={{ maxWidth: 480 }}>
                  <Form.Item name="matchId" label="Match ID" rules={[{ required: true }]}>
                    <InputNumber style={{ width: "100%" }} />
                  </Form.Item>
                  <Form.Item name="winnerTeam" label="Winner team" rules={[{ required: true }]}>
                    <Input placeholder="India" />
                  </Form.Item>
                  <Button type="primary" loading={settlingMatch} onClick={onSettleMatch}>
                    Settle Match Bets
                  </Button>
                </Form>
              </Card>
            ),
          },
        ]}
      />

      {maint ? (
        <Modal
          title={`#${editing?.betId || ""}`}
          open={!!editing}
          onCancel={() => setEditing(null)}
          onOk={onSaveEdit}
          confirmLoading={updating}
          destroyOnClose>
          <Form form={editForm} layout="vertical">
            <Form.Item name="selection" label="Selection" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="betType" label="Type" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: "back", label: "Back / Yes" },
                  { value: "lay", label: "Lay / No" },
                ]}
              />
            </Form.Item>
            <Form.Item name="marketType" label="Market">
              <Select
                options={[
                  { value: "fancy", label: "Fancy" },
                  { value: "bookmaker", label: "Bookmaker" },
                  { value: "match_odds", label: "Match Odds" },
                ]}
              />
            </Form.Item>
            <Form.Item name="odds" label="Odds / Rate" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} min={0.01} />
            </Form.Item>
            <Form.Item name="stake" label="Stake" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} min={1} />
            </Form.Item>
          </Form>
        </Modal>
      ) : null}
    </div>
  );
};

export default BetSettlementAdmin;
